import { Button } from '@material-ui/core';
import * as React from 'react';
import { usePageDialog } from '../../hooks/use-page-dialog';

interface AgentResultsErrorBoundaryProps {
    children: React.ReactNode;
}

interface AgentResultsErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

const AgentErrorSummary = ({ error }: { error: string }) => {
    const { onClose } = usePageDialog();
    return (
        <div style={{ padding: '40px', margin: '0 auto', maxWidth: '480px' }}>
            <h3>Unable to display results</h3>
            <p>
                <p>
                    There was an error rendering the agent results. This may occur when the data structure from the AI
                    response is unexpected.
                </p>
                <p>
                    <strong>Error:</strong> {error}
                </p>
                <p>Please try:</p>
                <ul>
                    <li>Asking the agent to reformulate the query</li>
                    <li>Simplifying your request</li>
                    <li>Refreshing the page if the issue persists</li>
                </ul>
            </p>
            <Button onClick={onClose}>Close</Button>
        </div>
    );
};

/**
 * Error boundary for agent results components (table and chart).
 * Catches rendering errors that may occur when displaying LLM-generated data
 * and shows a user-friendly error dialog instead of crashing the entire page.
 */
export class AgentResultsErrorBoundary extends React.Component<
    AgentResultsErrorBoundaryProps,
    AgentResultsErrorBoundaryState
> {
    static getDerivedStateFromError(error: Error): AgentResultsErrorBoundaryState {
        return {
            error,
            hasError: true
        };
    }

    constructor(props: AgentResultsErrorBoundaryProps) {
        super(props);
        this.state = {
            error: null,
            hasError: false
        };
    }

    render() {
        if (this.state.hasError) {
            return <AgentErrorSummary error={this.state.error?.message || 'Unknown error'} />;
        }

        return this.props.children;
    }
}
